import { z } from "zod";

/**
 * Validation primitives with no capabilities: no filesystem, no process state,
 * no clock. `io.ts` holds the same concerns that need a capability and re-exports
 * everything here, so callers that want both keep one import.
 *
 * Kept separate because a module is the unit a bundler pulls in. Importing one
 * pure predicate from a module that also opens files drags the filesystem along
 * with it, which is fatal for anything that has to run in a workflow sandbox.
 */

export const JsonValueSchema = z.json();
export const JsonObjectSchema = z.record(z.string(), JsonValueSchema);
export type JsonValue = z.infer<typeof JsonValueSchema>;
export type JsonObject = z.infer<typeof JsonObjectSchema>;

export function sortJson(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(sortJson);
  if (value !== null && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [key, sortJson(item)]),
    );
  return value;
}

export class BoundaryError extends Error {
  constructor(
    readonly boundary: string,
    readonly fields: string[],
  ) {
    super(`${boundary}: invalid ${fields.join(", ")}`);
    this.name = "BoundaryError";
  }
}

export function parse<T extends z.ZodType>(
  schema: T,
  value: unknown,
  boundary: string,
): z.output<T> {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new BoundaryError(
      boundary,
      result.error.issues.map((issue) => `${issue.path.join(".") || "root"} (${issue.code})`),
    );
  }
  return result.data;
}

/**
 * Deep copy of parsed JSON data, used where a caller must not be able to mutate
 * what it was handed.
 *
 * Not `structuredClone`: the workflow sandbox does not define it, and that is
 * invisible until a run activates. Everything cloned here has already been
 * through a Zod parse, so it is JSON and the round trip is exact.
 */
export function deepClone<T>(value: T): T {
  const copy: T = JSON.parse(JSON.stringify(value));
  return copy;
}

export const RunIdSchema = z.string().regex(/^[a-f0-9]{32}$/);

export function isIana(value: unknown): value is string {
  if (typeof value !== "string" || (value !== "UTC" && !value.includes("/"))) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}
